import { useState, useCallback, useRef } from 'react';
import { WorkflowStep, AnalysisStatus, TrainingStatus } from '../types';
import { DEFAULT_MODEL_CONFIG, DEFAULT_TRAINING_SPLIT } from '../config/constants';
import { ModelParameters } from '../components/ModelConfiguration';
import { TrainingDataSplitData } from '../components/TrainingDataSplit';
import { TrainingStatusResponse, TrainingResultsInfo } from './useSessionManagement';

// [WORKFLOW_DEBUG] Debug flag - set to true for debugging progress issues
const DEBUG_PROGRESS = true;

export const useTrainingWorkflow = () => {
    const [workflowStep, setWorkflowStep] = useState<WorkflowStep>('upload');
    const [analysisStatus, setAnalysisStatus] = useState<AnalysisStatus>('idle');
    const [analysisProgress, setAnalysisProgress] = useState<number>(0);
    const [analysisCurrentStep, setAnalysisCurrentStep] = useState<string>('');
    const [analysisEta, setAnalysisEta] = useState<number | null>(null);

    // Ref to track current progress for monotonic updates (avoids stale closure)
    const progressRef = useRef<number>(0);
    const [trainingStatus, setTrainingStatus] = useState<TrainingStatus>('idle');
    const [isGeneratingDatasets, setIsGeneratingDatasets] = useState<boolean>(false);
    const [isTrainingModels, setIsTrainingModels] = useState<boolean>(false);
    const [hasTrainedModel, setHasTrainedModel] = useState<boolean>(false);
    const [datasetCount, setDatasetCount] = useState<number>(0);
    const [violinPlots, setViolinPlots] = useState<Record<string, string>>({});
    const [resultsLoaded, setResultsLoaded] = useState<boolean>(false);
    const [modelConfiguration, setModelConfiguration] = useState<ModelParameters>(DEFAULT_MODEL_CONFIG);
    const [trainingDataSplit, setTrainingDataSplit] = useState<TrainingDataSplitData>({
        n_train: DEFAULT_TRAINING_SPLIT.trainPercentage,
        n_val: DEFAULT_TRAINING_SPLIT.valPercentage,
        n_test: DEFAULT_TRAINING_SPLIT.testPercentage,
        random_dat: false,
        trainPercentage: DEFAULT_TRAINING_SPLIT.trainPercentage,
        valPercentage: DEFAULT_TRAINING_SPLIT.valPercentage,
        testPercentage: DEFAULT_TRAINING_SPLIT.testPercentage
    });

    /**
     * Return to upload phase without clearing files/config.
     * Allows user to adjust parameters and re-generate datasets.
     */
    const returnToUpload = useCallback(() => {
        if (DEBUG_PROGRESS) {
            console.log('[PROGRESS] RETURN TO UPLOAD');
        }
        progressRef.current = 0;
        setWorkflowStep('upload');
        setAnalysisStatus('idle');
        setAnalysisProgress(0);
        setAnalysisCurrentStep('');
        setAnalysisEta(null);
        setIsGeneratingDatasets(false);
        setIsTrainingModels(false);
        setHasTrainedModel(false);
        setTrainingStatus('idle');
        setViolinPlots({});
        setResultsLoaded(false);
        setDatasetCount(0);
    }, []);

    const resetWorkflow = useCallback(() => {
        if (DEBUG_PROGRESS) {
            console.log('[PROGRESS] WORKFLOW RESET');
        }
        progressRef.current = 0;
        setWorkflowStep('upload');
        setAnalysisStatus('idle');
        setAnalysisProgress(0);
        setAnalysisCurrentStep('');
        setAnalysisEta(null);
        setTrainingStatus('idle');
        setIsGeneratingDatasets(false);
        setIsTrainingModels(false);
        setHasTrainedModel(false);
        setDatasetCount(0);
        setViolinPlots({});
        setResultsLoaded(false);
        setModelConfiguration(DEFAULT_MODEL_CONFIG);
        setTrainingDataSplit({
            n_train: DEFAULT_TRAINING_SPLIT.trainPercentage,
            n_val: DEFAULT_TRAINING_SPLIT.valPercentage,
            n_test: DEFAULT_TRAINING_SPLIT.testPercentage,
            random_dat: false,
            trainPercentage: DEFAULT_TRAINING_SPLIT.trainPercentage,
            valPercentage: DEFAULT_TRAINING_SPLIT.valPercentage,
            testPercentage: DEFAULT_TRAINING_SPLIT.testPercentage
        });
    }, []);

    /**
     * Update analysis progress - MONOTONIC only (prevents oscillation)
     * Only accepts progress values >= current progress, unless force=true
     */
    const updateAnalysisProgress = useCallback((progress: number, currentStep: string, eta?: number, force?: boolean) => {
        const currentProgress = progressRef.current;

        // Debug logging
        if (DEBUG_PROGRESS) {
            const action = force ? 'FORCE' : (progress >= currentProgress ? 'ACCEPT' : 'REJECT');
            console.log(`[PROGRESS] ${action}: ${currentProgress}% → ${progress}% | step: "${currentStep}" | eta: ${eta ?? 'n/a'}`);
        }

        // Only update if progress is increasing OR force is true (for resets)
        if (progress >= currentProgress || force) {
            progressRef.current = progress;
            setAnalysisProgress(progress);
            setAnalysisCurrentStep(currentStep);
            setAnalysisEta(eta ?? null);
        }
    }, []);

    /**
     * Reset progress to 0 (use this instead of updateAnalysisProgress(0, ...) for resets)
     */
    const resetAnalysisProgress = useCallback(() => {
        if (DEBUG_PROGRESS) {
            console.log(`[PROGRESS] RESET: ${progressRef.current}% → 0%`);
        }
        progressRef.current = 0;
        setAnalysisProgress(0);
        setAnalysisCurrentStep('');
        setAnalysisEta(null);
    }, []);

    const completeDatasetGeneration = useCallback((plots: Record<string, string>, count: number) => {
        console.log('[WORKFLOW_DEBUG] completeDatasetGeneration called', { plotCount: Object.keys(plots).length, count });
        progressRef.current = 100;
        setIsGeneratingDatasets(false);
        setViolinPlots(plots);
        setDatasetCount(count);
        setAnalysisStatus('completed');
        setAnalysisProgress(100);
        setResultsLoaded(true);
        // Only set phase2 if we're not already past it (phase4 = model trained)
        setWorkflowStep(prev => {
            if (prev === 'phase4' || prev === 'phase3') {
                console.log('[WORKFLOW_DEBUG] Keeping workflow at', prev, '(already past phase2)');
                return prev;
            }
            console.log('[WORKFLOW_DEBUG] Workflow step set to phase2 (violin plots displayed)');
            return 'phase2';
        });
    }, []);

    const completeModelTraining = useCallback(() => {
        setIsTrainingModels(false);
        setTrainingStatus('completed');
        setHasTrainedModel(true);
        setWorkflowStep('phase4');
    }, []);

    const returnToModelConfig = useCallback(() => {
        if (DEBUG_PROGRESS) {
            console.log('[PROGRESS] RETURN TO MODEL CONFIG');
        }
        progressRef.current = 0;
        setWorkflowStep('phase2');
        setHasTrainedModel(false);
        setTrainingStatus('idle');
        setIsTrainingModels(false);
        setAnalysisProgress(0);
        setAnalysisCurrentStep('');
        setAnalysisEta(null);
        setResultsLoaded(false);
        // Preserve: violinPlots, datasetCount, modelConfiguration, trainingDataSplit
    }, []);

    /**
     * Restore workflow state from session data
     * [WORKFLOW_DEBUG] Now uses workflow_phase from sessions table for reliable restoration
     *
     * Priority:
     * 1. Active process (training_progress table) - show progress bar
     * 2. Persisted workflow_phase (sessions table) - restore to that phase
     * 3. Metadata fallback (backwards compatibility) - infer from hasTrainedModel/hasViolinPlots
     */
    const restoreWorkflowState = useCallback((
        trainingStatus: TrainingStatusResponse,
        resultsInfo: TrainingResultsInfo
    ) => {
        console.log('[WORKFLOW_DEBUG] restoreWorkflowState called', {
            trainingStatus: trainingStatus.status,
            workflowPhase: resultsInfo.workflowPhase,
            hasViolinPlots: resultsInfo.hasViolinPlots,
            hasTrainedModel: resultsInfo.hasTrainedModel
        });

        // [WORKFLOW_DEBUG] Priority 1: Active process (training_progress table)
        if (trainingStatus.status === 'in_progress') {
            const currentStep = trainingStatus.current_step?.toLowerCase() || '';

            // Check if it's dataset generation
            const isDatasetGeneration =
                currentStep.includes('dataset generation') ||
                currentStep.includes('violin');

            // Model training indicators (only if NOT dataset generation)
            const isModelTraining = !isDatasetGeneration && (
                currentStep.includes('epoch') ||
                currentStep.includes('post_training') ||
                currentStep.includes('model training') ||
                currentStep.includes('training_') ||
                currentStep.includes('evaluating')
            );

            if (isModelTraining) {
                console.log('[WORKFLOW_DEBUG] Active model training detected, restoring to phase3');
                setWorkflowStep('phase3');  // [WORKFLOW_DEBUG] Was phase5, now phase3
                setIsTrainingModels(true);
                setIsGeneratingDatasets(false);
                setTrainingStatus('running');
                setAnalysisStatus('completed'); // Datasets must be done if training started
            } else {
                console.log('[WORKFLOW_DEBUG] Active dataset generation detected, restoring to phase1');
                setWorkflowStep('phase1');  // [WORKFLOW_DEBUG] Was phase3, now phase1
                setIsGeneratingDatasets(true);
                setIsTrainingModels(false);
                setAnalysisStatus('running');
            }

            progressRef.current = trainingStatus.progress;
            setAnalysisProgress(trainingStatus.progress);
            setAnalysisCurrentStep(trainingStatus.current_step || 'Processing...');
            return;
        }

        // [WORKFLOW_DEBUG] Priority 2: Persisted workflow_phase from sessions table
        const persistedPhase = resultsInfo.workflowPhase;
        if (persistedPhase && persistedPhase !== 'upload') {
            console.log('[WORKFLOW_DEBUG] Using persisted workflow_phase:', persistedPhase);

            // If completed, reset to upload (start fresh)
            // [WORKFLOW_DEBUG] Don't set hasTrainedModel - we want a clean slate
            if (persistedPhase === 'completed') {
                console.log('[WORKFLOW_DEBUG] Session completed, resetting to upload (clean slate)');
                setWorkflowStep('upload');
                setHasTrainedModel(false);  // [WORKFLOW_DEBUG] Clean slate - don't show old results
                setAnalysisStatus('idle');   // [WORKFLOW_DEBUG] Reset to idle, not completed
                setTrainingStatus('idle');   // [WORKFLOW_DEBUG] Reset to idle, not completed
                setIsGeneratingDatasets(false);
                setIsTrainingModels(false);
                return;
            }

            // phase1 and phase3 are active processing phases (dataset gen / model training).
            // If we reach Priority 2 it means training_progress has no active process,
            // so these phases are stale — reset to upload.
            if (persistedPhase === 'phase1' || persistedPhase === 'phase3') {
                console.log('[WORKFLOW_DEBUG] Stale active phase detected:', persistedPhase, '→ resetting to upload');
                setWorkflowStep('upload');
                setIsGeneratingDatasets(false);
                setIsTrainingModels(false);
                setAnalysisStatus('idle');
                setTrainingStatus('idle');
                return;
            }

            // Restore to persisted phase
            setWorkflowStep(persistedPhase as WorkflowStep);
            setIsGeneratingDatasets(false);
            setIsTrainingModels(false);

            if (persistedPhase === 'phase2' || persistedPhase === 'phase4') {
                setAnalysisStatus('completed');
                progressRef.current = 100;
                setAnalysisProgress(100);
                setAnalysisCurrentStep('Datasets generated');

                // n_dat comes from DB, set it independently of violin plots
                if (resultsInfo.datasetCount > 0) {
                    setDatasetCount(resultsInfo.datasetCount);
                }

                if (resultsInfo.violinPlots) {
                    setViolinPlots(resultsInfo.violinPlots as Record<string, string>);
                    setResultsLoaded(true);
                }

                // [WORKFLOW_DEBUG] For phase4 (Plotting Interface), also set hasTrainedModel
                // so TrainingResults component is shown
                if (persistedPhase === 'phase4') {
                    setHasTrainedModel(true);
                    setTrainingStatus('completed');
                    console.log('[WORKFLOW_DEBUG] phase4 restored: hasTrainedModel=true, trainingStatus=completed');
                }
            }
            return;
        }

        // [WORKFLOW_DEBUG] Priority 3: Metadata fallback (backwards compatibility)
        // [WORKFLOW_DEBUG] If hasTrainedModel, reset to upload with clean slate
        if (resultsInfo.hasTrainedModel) {
            console.log('[WORKFLOW_DEBUG] Fallback: hasTrainedModel, resetting to upload (clean slate)');
            setWorkflowStep('upload');  // [WORKFLOW_DEBUG] Completed → reset to upload
            setHasTrainedModel(false);  // [WORKFLOW_DEBUG] Clean slate - don't show old results
            setAnalysisStatus('idle');   // [WORKFLOW_DEBUG] Reset to idle
            setTrainingStatus('idle');   // [WORKFLOW_DEBUG] Reset to idle
            setIsGeneratingDatasets(false);
            setIsTrainingModels(false);
            return;
        }

        if (resultsInfo.hasViolinPlots) {
            console.log('[WORKFLOW_DEBUG] Fallback: hasViolinPlots, setting phase2');
            setWorkflowStep('phase2');  // [WORKFLOW_DEBUG] Was phase4, now phase2
            setAnalysisStatus('completed');
            setIsGeneratingDatasets(false);
            setIsTrainingModels(false);
            progressRef.current = 100;
            setAnalysisProgress(100);
            setAnalysisCurrentStep('Datasets generated');

            // n_dat comes from DB, set it independently of violin plots
            if (resultsInfo.datasetCount > 0) {
                setDatasetCount(resultsInfo.datasetCount);
            }

            if (resultsInfo.violinPlots) {
                setViolinPlots(resultsInfo.violinPlots as Record<string, string>);
                setResultsLoaded(true);
            }
            return;
        }

        console.log('[WORKFLOW_DEBUG] No state to restore, keeping default upload');
    }, [setWorkflowStep, setIsTrainingModels, setIsGeneratingDatasets, setTrainingStatus,
        setAnalysisStatus, setHasTrainedModel, setAnalysisProgress, setAnalysisCurrentStep,
        setViolinPlots, setDatasetCount, setResultsLoaded]);

    return {
        workflowStep,
        setWorkflowStep,
        analysisStatus,
        setAnalysisStatus,
        analysisProgress,
        analysisCurrentStep,
        analysisEta,
        updateAnalysisProgress,
        resetAnalysisProgress,
        trainingStatus,
        setTrainingStatus,
        isGeneratingDatasets,
        setIsGeneratingDatasets,
        isTrainingModels,
        setIsTrainingModels,
        hasTrainedModel,
        datasetCount,
        violinPlots,
        modelConfiguration,
        setModelConfiguration,
        trainingDataSplit,
        setTrainingDataSplit,
        resetWorkflow,
        returnToUpload,
        returnToModelConfig,
        completeDatasetGeneration,
        completeModelTraining,
        resultsLoaded,
        restoreWorkflowState
    };
};